import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  Select,
  Input,
  Row,
  Col,
  Button,
  message,
  Spin,
  Space,
} from "antd";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import axios from "axios";
import debounce from "lodash/debounce";
import { updateStock } from "../../../../Services/StockService";
import { FETCH_ALL_STOCK_API } from "../../../../Constants/Apis";

const StockMutateCreationForm = ({
  modalForm,
  ColorData,
  data,
  callReFetch,
  userData,
  scannerActive,
  onScanBarcode, // Renamed from handleScanBarcode to avoid duplicate identifier
  setScannerActive,
  setBarcodeValue,
  isEditMode = false,
  isTempEditMode = false,
  onClose,
  onStockSelect,
  colorLoading = false,
  senderCounterCode,
}) => {
  const [size, setSize] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedStock, setSelectedStock] = useState(null);

  useEffect(() => {
    if (isEditMode && data) {
      modalForm.setFieldsValue({
        barcode: data.barcode,
        article: data.article,
        color: data.color,
        assort: data.assort, // Add this line to set the assort field in the form
        size: data.size,
        price: data.price,
        quantity: data.quantity,
        currentQuantity: data.currentQuantity,
        receivedQuantity: data.receivedQuantity,
      });
      setSize(data.size);
    }
  }, [isEditMode, data]);

  const handleSizeChange = (e) => {
    let value = e.target.value;
    if (/^\d{0,2}$/.test(value)) {
      if (value.length === 1) {
        value = `0${value}`;
      }
      if (Number(value) <= 99) {
        setSize(value);
      }

      // If value is 2 digits, auto-fill assort to 1
      if (value.length === 2) {
        modalForm.setFieldsValue({ assort: "1" });
      }
      // If size is empty, clear assort
      if (!value) {
        modalForm.setFieldsValue({ assort: "" });
      }
    }
  };

  // Fetch stock data with search keyword using the dropdown endpoint
  const fetchStocks = async (searchKeyword = "") => {
    try {
      setStockLoading(true);
      // Use the new dropdown endpoint with counterCode from userData
      const response = await axios.get(
        `${FETCH_ALL_STOCK_API}/cc/${senderCounterCode}/dropdown`,
        {
          params: { searchKeyword },
        }
      );

      // Extract the data from the response
      // The dropdown endpoint returns paginated data in the format { data: [...], pagination: {...} }
      let stocks = [];

      // Check if the response has the expected structure
      if (response.data && Array.isArray(response.data)) {
        // Old format - direct array
        stocks = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        // New format - paginated response with data property
        stocks = response.data.data;
      }

      // Aggregate stocks by barcode and sum quantities
      const aggregatedStocks = stocks.reduce((acc, stock) => {
        const barcode = stock.barcode;
        
        if (acc[barcode]) {
          // If barcode already exists, sum the quantities
          acc[barcode].currentQuantity = (acc[barcode].currentQuantity || 0) + (stock.currentQuantity || 0);
        } else {
          // If barcode doesn't exist, add it to the accumulator
          acc[barcode] = { ...stock };
        }
        
        return acc;
      }, {});

      // Convert back to array
      const uniqueStocks = Object.values(aggregatedStocks);

      setStockOptions(uniqueStocks);
      return uniqueStocks;
    } catch (error) {
      console.error("Error fetching stocks:", error);
      message.error("Failed to fetch stocks");
      return [];
    } finally {
      setStockLoading(false);
    }
  };

  // Debounced search function
  const debouncedFetchStocks = useMemo(
    () => debounce((searchValue) => fetchStocks(searchValue), 500),
    []
  );

  // Initial fetch of stocks and re-fetch when senderCounterCode changes
  useEffect(() => {
    // Only fetch if we have a valid senderCounterCode
    if (senderCounterCode) {
      // Show loading state
      setStockLoading(true);
      console.log("Fetching stocks for counter:", senderCounterCode);

      // Reset the form fields related to item selection
      if (!isEditMode) {
        modalForm.setFieldsValue({
          article: undefined,
          color: undefined,
          size: undefined,
          assort: undefined,
          price: undefined,
          quantity: undefined,
        });
      }

      // Reset selected stock
      setSelectedStock(null);

      // Fetch stocks for the selected sender
      fetchStocks().finally(() => {
        // Ensure loading state is turned off even if there's an error
        setStockLoading(false);
      });
    }

    // Cleanup debounce on unmount
    return () => {
      debouncedFetchStocks.cancel();
    };
  }, [senderCounterCode]); // Re-run when senderCounterCode changes

  // Handle stock selection
  const handleStockSelect = (value, option) => {
    console.log("Selected value:", value);
    console.log("Stock options:", stockOptions);

    const stock = stockOptions.find((s) => s.barcode === value);
    console.log("Found stock:", stock); // Debug log

    setSelectedStock(stock);

    if (stock) {
      // Ensure we set all the required fields
      modalForm.setFieldsValue({
        barcode: stock.barcode,
        article: stock.article,
        color: stock.color,
        size: stock.size,
        price: stock.price || 0,
      });

      // Make sure to update the size state
      setSize(stock.size);

      // Display available quantity information
      const currentQty =
        stock.currentQuantity !== undefined ? Number(stock.currentQuantity) : 0;
      if (currentQty <= 0) {
        message.warning(`This item has no available quantity (${currentQty})`);
      } else {
        message.info(`Available quantity: ${currentQty}`);
      }

      // Pass the selected stock back to the parent component
      if (onStockSelect) {
        onStockSelect(stock);
      }
    }
  };

  // Handle stock search
  const handleStockSearch = (value) => {
    setSearchText(value);
    if (value) {
      debouncedFetchStocks(value);
    } else {
      // If search is cleared, fetch all stocks
      fetchStocks();
    }
  };

  // Handle barcode scanning internally
  const handleBarcodeScanned = (err, result) => {
    if (result) {
      const scannedBarcode = result.text;

      // Find the stock with this barcode
      const stock = stockOptions.find((s) => s.barcode === scannedBarcode);

      if (stock) {
        // If found, populate all fields
        modalForm.setFieldsValue({
          barcode: stock.barcode,
          article: stock.article,
          color: stock.color,
          size: stock.size,
          price: stock.price || 0,
        });
        setSize(stock.size);

        // Pass the selected stock back to the parent component
        if (onStockSelect) {
          onStockSelect(stock);
        }
      } else {
        // If not found in current options, just set the barcode and search for it
        modalForm.setFieldsValue({ barcode: scannedBarcode });
        fetchStocks(scannedBarcode);
      }

      // Call the parent's scan handler if provided
      if (onScanBarcode) {
        onScanBarcode(err, result);
      }

      modalForm.validateFields(["barcode"]); // Validate field to remove error
      setScannerActive(false); // Stop scanner
    }
  };

  const handleUpdateStock = async (values) => {
    try {
      const { quantity, currentQuantity } = values;
      const updateData = {
        currentQuantity: Number(currentQuantity),
        _id: values._id,
      };

      // Call API to update stock
      await updateStock(updateData._id, updateData);

      // Show success message and reset form
      message.success("Stock updated successfully!");
      modalForm.resetFields();

      // Close the modal after successful update
      setIsModalVisible(false);

      // If onClose prop is provided (from parent component), call it to close the modal
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      message.error("Failed to update stock. Please try again.");
    }

    callReFetch();
  };

  return (
    <>
      {/* Global loading indicator */}
      {stockLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            padding: "8px",
            textAlign: "center",
            backgroundColor: "#e6f7ff",
            borderBottom: "1px solid #91d5ff",
            zIndex: 1000,
          }}
        >
          <Space>
            <Spin size="small" />
            <span>Loading stock data...</span>
          </Space>
        </div>
      )}
      <Form form={modalForm} layout="vertical">
        <Form.Item
          label="Barcode"
          name="barcode"
          rules={[{ required: true, message: "Please input Barcode!" }]}
        >
          <Row gutter={[8, 8]}>
            {" "}
            {/* Add Row with gutter for spacing */}
            <Col span={24}>
              {" "}
              {/* Full width for the Select on the first row */}
              <Select
                showSearch
                placeholder="Search for barcode or article"
                onChange={handleStockSelect}
                onSearch={handleStockSearch}
                optionFilterProp="children"
                allowClear
                disabled={isEditMode}
                loading={stockLoading}
                notFoundContent={
                  stockLoading ? <Spin size="small" /> : "No stock found"
                }
                filterOption={false} // Disable browser-side filtering
                style={{ width: "100%" }} // Ensure it takes full width of its column
                suffixIcon={
                  stockLoading ? <LoadingOutlined /> : <SearchOutlined />
                }
              >
                {stockOptions.map((stock) => (
                  <Select.Option key={stock.barcode} value={stock.barcode}>
                    {stock.barcode} ({stock.article} - {stock.color}) - Qty: {stock.currentQuantity || 0}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              {" "}
              {/* Full width for the Button on the second row */}
              <Button
                onClick={() => setScannerActive(true)}
                disabled={scannerActive || isEditMode}
                style={{ width: "100%" }} // Ensure it takes full width of its column
              >
                Scan
              </Button>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item
          label="Article"
          name="article"
          rules={[{ required: true, message: "Please input Article!" }]}
        >
          <Input placeholder="Article" disabled={true} />
        </Form.Item>

        <Form.Item
          label="Warna"
          name="color"
          rules={[{ required: true, message: "Please input Warna!" }]}
        >
          <Select
            placeholder="Select a color"
            disabled={true}
            loading={colorLoading}
            suffixIcon={colorLoading ? <LoadingOutlined /> : null}
          >
            {Array.isArray(ColorData) && ColorData.length > 0 ? (
              ColorData.map((color) => (
                <Select.Option key={color.name} value={color.name}>
                  {color.name}
                </Select.Option>
              ))
            ) : (
              <Select.Option value="loading" disabled>
                {colorLoading ? "Loading colors..." : "No colors available"}
              </Select.Option>
            )}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Size"
              name="size"
              rules={[{ required: true, message: "Please input Size!" }]}
            >
              <Input
                value={size}
                onChange={handleSizeChange}
                maxLength={2}
                disabled={true}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Assort"
              name="assort"
              rules={[{ required: true, message: "Please input Assort!" }]}
            >
              <Input
                type="number"
                disabled={isEditMode}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Quantity Kirim"
              name="quantity"
              rules={[
                { required: true, message: "Please input Quantity Kirim!" },
                {
                  validator: (_, value) => {
                    // Convert value to number for comparison
                    const quantityValue = Number(value);

                    // Basic validation for positive numbers
                    if (isNaN(quantityValue) || quantityValue <= 0) {
                      return Promise.reject(
                        "Quantity must be a positive number"
                      );
                    }

                    console.log("selectedStock", selectedStock);

                    // Check if we have a selected stock
                    if (selectedStock) {
                      // If currentQuantity is undefined or null, treat it as 0
                      const currentQty = selectedStock.currentQuantity !== undefined && 
                                       selectedStock.currentQuantity !== null ? 
                                       Number(selectedStock.currentQuantity) : 0;

                      console.log("currentQty", currentQty);

                      // If current quantity is 0, no quantity can be entered
                      if (currentQty === 0) {
                        return Promise.reject(
                          `No stock available (current quantity: ${currentQty})`
                        );
                      }

                      // If current quantity is negative, no quantity can be entered
                      if (currentQty < 0) {
                        return Promise.reject(
                          `Invalid stock quantity (${currentQty})`
                        );
                      }

                      // Validate that quantity doesn't exceed current quantity
                      if (quantityValue > currentQty) {
                        return Promise.reject(
                          `Cannot exceed available quantity (${currentQty})`
                        );
                      }
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                onWheel={(e) => e.target.blur()}
                readOnly={isEditMode}
                min="1"
              />
            </Form.Item>
          </Col>
          {isEditMode && (
            <Col span={12}>
              <Form.Item
                label="Current Quantity"
                name="currentQuantity"
                rules={[
                  { required: true, message: "Please input Current Quantity!" },
                ]}
              >
                <Input type="number" onWheel={(e) => e.target.blur()} />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              label="Harga"
              name="price"
              rules={[{ required: true, message: "Please input Harga!" }]}
            >
              <Input
                prefix="Rp. "
                type="number"
                disabled
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>
          </Col>
        </Row>
        {isEditMode && (
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => {
                modalForm.validateFields().then((values) => {
                  handleUpdateStock({
                    ...values,
                    _id: data._id,
                  });
                });
              }}
            >
              Update Stock
            </Button>
          </Form.Item>
        )}
      </Form>
    </>
  );
};

export default StockMutateCreationForm;
